import { readJson, writeJson } from '../lib/storage';

export type Action = 'forward' | 'back' | 'left' | 'right' | 'fire';
export const ACTIONS: readonly Action[] = ['forward', 'back', 'left', 'right', 'fire'];
export const ACTION_LABELS: Record<Action, string> = {
  forward: 'Forward',
  back: 'Reverse',
  left: 'Rotate left',
  right: 'Rotate right',
  fire: 'Fire',
};

/** Physical key codes (KeyboardEvent.code), so layouts like AZERTY keep the same positions. */
export type PlayerBindings = Record<Action, string>;
export type Bindings = [PlayerBindings, PlayerBindings];

export const DEFAULT_BINDINGS: Bindings = [
  { forward: 'KeyW', back: 'KeyS', left: 'KeyA', right: 'KeyD', fire: 'Space' },
  { forward: 'ArrowUp', back: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight', fire: 'Enter' },
];

/** Keys that can't be bound: they pause the game or move focus. */
export const RESERVED_KEYS = new Set(['Escape', 'Tab', 'MetaLeft', 'MetaRight', 'ContextMenu']);

const STORAGE_KEY = 'tpubg:tank:bindings';

export function loadBindings(): Bindings {
  const saved = readJson<unknown>(STORAGE_KEY, null);
  if (!Array.isArray(saved) || saved.length !== 2) return clone(DEFAULT_BINDINGS);
  const ok = saved.every((p) => p && ACTIONS.every((a) => typeof (p as Record<string, unknown>)[a] === 'string'));
  return ok ? (saved as Bindings) : clone(DEFAULT_BINDINGS);
}

export function saveBindings(b: Bindings): void {
  writeJson(STORAGE_KEY, b);
}

export function clone(b: Bindings): Bindings {
  return [{ ...b[0] }, { ...b[1] }];
}

/** Assigns `code` to an action; if another action already uses it, the two swap keys. */
export function assignKey(b: Bindings, player: 0 | 1, action: Action, code: string): Bindings {
  const next = clone(b);
  const previous = next[player][action];
  for (const p of [0, 1] as const) {
    for (const a of ACTIONS) {
      if (next[p][a] === code) next[p][a] = previous;
    }
  }
  next[player][action] = code;
  return next;
}

const NAMED: Record<string, string> = {
  Space: 'Space',
  Enter: 'Enter',
  NumpadEnter: 'Num Enter',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  ShiftLeft: 'L Shift',
  ShiftRight: 'R Shift',
  ControlLeft: 'L Ctrl',
  ControlRight: 'R Ctrl',
  AltLeft: 'L Alt',
  AltRight: 'R Alt',
  Backspace: 'Backspace',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
};

export function keyLabel(code: string): string {
  if (NAMED[code]) return NAMED[code];
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}

export function keyName(code: string): string {
  const arrows: Record<string, string> = { ArrowUp: 'Up arrow', ArrowDown: 'Down arrow', ArrowLeft: 'Left arrow', ArrowRight: 'Right arrow' };
  return arrows[code] ?? keyLabel(code);
}
